"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBestRpcTmClient = exports.QueueManager = exports.callIgnoreError = exports.modifyTmClient = exports.toTxHash = exports.computeTxHash = exports.appendHexPrefix = exports.stripHexPrefix = exports.sortObject = exports.overrideConfig = void 0;
const crypto_1 = require("@cosmjs/crypto");
const tendermint_rpc_1 = require("@cosmjs/tendermint-rpc");
const overrideConfig = (defaults, override) => {
    const result = Object.assign({}, defaults);
    if (!override)
        return result;
    for (const key of Object.keys(override)) {
        const member = override[key];
        if (typeof member === "undefined")
            continue;
        if (typeof member === "object") {
            result[key] = (0, exports.overrideConfig)(result[key], member);
        }
        else {
            result[key] = member;
        }
    }
    return result;
};
exports.overrideConfig = overrideConfig;
const sortObject = (input) => {
    if (typeof input !== "object" || input === null)
        return input;
    if (Array.isArray(input))
        return input.map(exports.sortObject);
    const output = {};
    Object.keys(input)
        .sort()
        .forEach((key) => (output[key] = (0, exports.sortObject)(input[key])));
    return output;
};
exports.sortObject = sortObject;
const stripHexPrefix = (input) => {
    return (input === null || input === void 0 ? void 0 : input.slice(0, 2)) === "0x" ? input.slice(2) : input;
};
exports.stripHexPrefix = stripHexPrefix;
const appendHexPrefix = (input) => {
    return (input === null || input === void 0 ? void 0 : input.slice(0, 2)) === "0x" ? input : `0x${input}`;
};
exports.appendHexPrefix = appendHexPrefix;
const computeTxHash = (bytes) => {
    if (!bytes)
        return bytes;
    return (0, exports.toTxHash)((0, crypto_1.sha256)(bytes));
};
exports.computeTxHash = computeTxHash;
const toTxHash = (bytes) => {
    if (!bytes)
        return bytes;
    return Buffer.from(bytes).toString("hex").toUpperCase();
};
exports.toTxHash = toTxHash;
/**
 * Make tendermint client send RPC requests with the same request
 * ID instead of using a random id that is unread in the client.
 *
 * Motivation: enhanced cache-ability on API providers.
 *
 * Broadcast methods are untouched to ensure retries don't hit cache.
 */
const modifyTmClient = (tmClient) => {
    try {
        const p = tmClient.p;
        const newEncoder = Object.assign({}, p);
        for (const method of Object.getOwnPropertyNames(p)) {
            if (method === "encodeBroadcastTx" || typeof p[method] !== "function")
                continue;
            const ogHandler = p[method];
            newEncoder[method] = (req) => {
                const result = ogHandler(req);
                if (req.method === "abci_query" && req.params.path === "/cosmos.auth.v1beta1.Query/Account") {
                    return result;
                }
                return Object.assign(Object.assign({}, result), { id: 42 });
            };
        }
        tmClient.p = newEncoder;
    }
    catch (error) {
        console.error("failed to modify tm client");
        console.error(error);
    }
    return tmClient;
};
exports.modifyTmClient = modifyTmClient;
const callIgnoreError = (runnable) => {
    return runnable();
};
exports.callIgnoreError = callIgnoreError;
class QueueManager {
    constructor(processor, options = {}) {
        var _a, _b;
        this.processor = processor;
        this.currTriggerThreshold = 0;
        this.currQueueTrigger = null;
        this.isProcessingQueue = false;
        this.queue = [];
        this.triggerDelay = (_a = options.triggerDelay) !== null && _a !== void 0 ? _a : 300;
        this.maxDelayThreshold = (_b = options.maxDelayThreshold) !== null && _b !== void 0 ? _b : 1000;
    }
    enqueue(task, skipTrigger = false) {
        this.queue.unshift(task);
        if (!skipTrigger)
            this.trigger();
    }
    trigger() {
        const currentTimeMillis = new Date().getTime();
        // do not shift delay later if next schedule
        if (this.currTriggerThreshold && currentTimeMillis + this.triggerDelay > this.currTriggerThreshold) {
            return;
        }
        if (!this.currTriggerThreshold && this.maxDelayThreshold > 0) {
            this.currTriggerThreshold = currentTimeMillis + this.maxDelayThreshold; // max wait for 1s before dispatching queue
        }
        clearTimeout(this.currQueueTrigger);
        this.currQueueTrigger = setTimeout(this.process.bind(this), this.triggerDelay);
    }
    process() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isProcessingQueue)
                return;
            this.isProcessingQueue = true; // activate sync lock
            this.currTriggerThreshold = 0; // reset to 0
            try {
                while (this.queue.length) {
                    const item = this.queue.pop();
                    try {
                        yield this.processor(item);
                    }
                    catch (error) {
                        console.error("queue manager process item failed");
                        console.error(error);
                    }
                }
            }
            finally {
                this.isProcessingQueue = false;
            }
        });
    }
}
exports.QueueManager = QueueManager;
const getBestRpcTmClient = (rpcUrls) => __awaiter(void 0, void 0, void 0, function* () {
    for (const rpcUrl of rpcUrls) {
        try {
            return {
                client: yield tendermint_rpc_1.Tendermint37Client.connect(rpcUrl),
                rpcUrl,
            };
        }
        catch (error) {
            console.debug("failed to connect RPC:" + rpcUrl);
            console.debug(error);
            continue;
        }
    }
    throw new Error("Could not get available RPC service");
});
exports.getBestRpcTmClient = getBestRpcTmClient;
